import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { I18nService } from '@biletix/core/services/i18n.service';
import { ApiResponse } from '@biletix/core/models/api-response';
import { ApiResponseStatus } from '@biletix/core/models/api-response-status';
import { SearchSuggestionsData } from '@biletix/core/models/search/search-suggestion.model';
import { SearchResultItem, SearchFilters } from '@biletix/core/models/search/search-result.model';
import { SearchListItem } from '@biletix/core/models/search/search-list-item.model';
import { SearchAggregationData } from '@biletix/core/models/search/search-aggregation.model';
import { SearchInput } from '@biletix/core/models/search/search-input.model';
import { SolrService } from '@biletix/core/services/solr.service';

@Injectable({ providedIn: 'root' })
export class SearchService {
  constructor(
    private i18n: I18nService,
    private solr: SolrService
  ) {}

  search(input: SearchInput): Observable<ApiResponse<SearchListItem[]>> {
    const lang = this.i18n.currentLang;
    const filters = this.toFilters(input);
    return this.solr.search(lang, filters, input.from ?? 0, input.pageSize ?? 10).pipe(
      map((items) => new ApiResponse({ status: ApiResponseStatus.SUCCESS, data: items.map((i) => this.toListItem(i)) }))
    );
  }

  searchWithAggregations(input: SearchInput): Observable<ApiResponse<{ items: SearchListItem[]; aggregations: SearchAggregationData }>> {
    const lang = this.i18n.currentLang;
    const filters = this.toFilters(input);
    return forkJoin({
      page: this.solr.search(lang, filters, input.from ?? 0, input.pageSize ?? 10),
      facets: this.solr.searchWithFacets(lang, filters),
    }).pipe(
      map(({ page, facets }) => {
        const aggregations: SearchAggregationData = { date: [], city: [], venue: [], category: [] };
        for (const [key, aggItems] of Object.entries(facets.aggregations)) {
          aggregations[key] = aggItems.map((i) => ({ key: i.key, label: i.label, count: i.count, category: i.category }));
        }
        return new ApiResponse({
          status: ApiResponseStatus.SUCCESS,
          data: { items: page.map((i) => this.toListItem(i)), aggregations },
        });
      })
    );
  }

  suggestions(keyword: string): Observable<ApiResponse<SearchSuggestionsData>> {
    const lang = this.i18n.currentLang;
    return this.solr.suggestions(lang, keyword).pipe(
      map((data) => new ApiResponse({ status: ApiResponseStatus.SUCCESS, data }))
    );
  }

  private toListItem(i: SearchResultItem): SearchListItem {
    return {
      id: i.id,
      groupId: i.groupId,
      name: i.name,
      groupName: i.groupName,
      shortName: i.shortName,
      groupShortName: i.groupShortName,
      category: i.category,
      subCategory: i.subCategory,
      dataType: i.dataType,
      lang: i.lang,
      image: i.image,
      groupImage: i.groupImage,
      status: i.status,
      venueCode: i.venueCode,
      liveEvent: i.liveEvent,
      eventDescription: i.eventDescription,
      firstActivePerfDate: i.firstActivePerfDate,
      start: i.start ?? i.firstActivePerfDate,
      lastActivePerfDate: i.lastActivePerfDate,
      end: i.end ?? i.lastActivePerfDate,
      cityNames: i.cityNames,
      venueNames: i.venueNames,
      venueShortNames: i.venueShortNames,
      regionNames: i.regionNames,
      perfDates: i.perfDates,
    };
  }

  private toArray(val: string | string[] | undefined | null): string[] {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }

  /* Filtre panelindeki kategori checkbox'ları her zaman pivot'un ikinci
     seviyesinden (alt kategori) gelir ve değeri Solr'daki subcategory
     alanının birebir karşılığıdır ("tiyatro" ya da ortama göre "tiyatro$ART").
     Bu yüzden hepsi subcategory fq'suna gönderilir. */
  private toFilters(input: SearchInput): SearchFilters {
    return {
      searchtext: input.searchtext ?? '',
      date: this.toArray(input.date),
      city: this.toArray(input.city),
      venue: this.toArray(input.venue),
      category: [],
      subcategory: this.toArray(input.category),
      maincategory: this.toArray(input.maincategory),
      gt: input.gt ?? undefined,
      lt: input.lt ?? undefined,
      region: input.region ?? undefined,
    };
  }
}
